import React, { useState } from 'react';
import {
  Switch,
  InputNumber,
  Typography,
  Flex,
  Collapse,
  ColorPicker,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import styles from './graphics-settings.module.css';

const { Panel } = Collapse;

interface CloudConfig {
  enabled: boolean;
  count: number;
  speed: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  opacity: number;
  colorHex: string;
  windDirectionX: number;
  windDirectionY: number;
  windDirectionZ: number;
  spawnAreaMinX: number;
  spawnAreaMinY: number;
  spawnAreaMinZ: number;
  spawnAreaMaxX: number;
  spawnAreaMaxY: number;
  spawnAreaMaxZ: number;
  threshold: number;
  range: number;
  steps: number;
  variationOpacity: number;
  variationScale: number;
  variationThreshold: number;
  variationColor: number;
}

interface CloudSettingsProps {
  currentCloudConfig: CloudConfig;
  callBack: (params: { type: string; value: any; key: string }) => void;
  type: string;
  setDisableBtn: (disabled: boolean) => void;
}

export default function CloudSettings({
  currentCloudConfig,
  callBack,
  type,
  setDisableBtn,
}: CloudSettingsProps) {
  const [isCloudEnabled, setIsCloudEnabled] = useState(
    currentCloudConfig?.enabled || false
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const handleChange = (key: string, value: any) => {
    callBack({ type, value, key });
    setDisableBtn(false);
  };

  const handleCloudToggle = (enabled: boolean) => {
    setIsCloudEnabled(enabled);
    handleChange('enabled', enabled);
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  if (!currentCloudConfig) {
    return null;
  }

  return (
    <>
      <Typography.Title level={5}>Cloud Settings</Typography.Title>

      <Flex justify='space-between' align='center' style={{ marginBottom: 16 }}>
        <Typography.Paragraph strong>Enable Clouds</Typography.Paragraph>
        <Switch checked={isCloudEnabled} onChange={handleCloudToggle} />
      </Flex>

      {isCloudEnabled && (
        <Flex vertical gap={16}>
          <div>
            <Typography.Paragraph strong>
              Cloud Count (1-50)
            </Typography.Paragraph>
            <InputNumber
              value={currentCloudConfig.count}
              min={1}
              max={50}
              style={{ width: '100%' }}
              onChange={(value) => handleChange('count', value)}
            />
          </div>

          <div>
            <Typography.Paragraph strong>
              Movement Speed (0.1-5.0)
            </Typography.Paragraph>
            <InputNumber
              value={currentCloudConfig.speed}
              min={0.1}
              max={5.0}
              step={0.1}
              style={{ width: '100%' }}
              onChange={(value) => handleChange('speed', value)}
            />
          </div>

          <div>
            <Typography.Paragraph strong>
              Opacity (0.0-1.0)
            </Typography.Paragraph>
            <InputNumber
              value={currentCloudConfig.opacity}
              min={0.0}
              max={1.0}
              step={0.01}
              style={{ width: '100%' }}
              onChange={(value) => handleChange('opacity', value)}
            />
          </div>

          <div>
            <Typography.Paragraph strong>Cloud Color</Typography.Paragraph>
            <ColorPicker
              showText
              value={currentCloudConfig.colorHex}
              onChange={(color) =>
                handleChange('colorHex', color.toHexString())
              }
              style={{ width: '100%' }}
            />
          </div>

          <div className={styles.optionsToggle} onClick={toggleAdvancedOptions}>
            {showAdvancedOptions ? (
              <>
                <UpOutlined /> Hide Advanced Cloud Options
              </>
            ) : (
              <>
                <DownOutlined /> Show Advanced Cloud Options
              </>
            )}
          </div>

          {showAdvancedOptions && (
            <Collapse ghost>
              <Panel header='Scale Settings' key='scale'>
                <Flex vertical gap={12}>
                  <div>
                    <Typography.Paragraph strong>
                      Scale X (50-1000)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.scaleX}
                      min={50}
                      max={1000}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('scaleX', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Scale Y (50-500)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.scaleY}
                      min={50}
                      max={500}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('scaleY', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Scale Z (50-1000)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.scaleZ}
                      min={50}
                      max={1000}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('scaleZ', value)}
                    />
                  </div>
                </Flex>
              </Panel>

              <Panel header='Wind Direction' key='wind'>
                <Flex vertical gap={12}>
                  <div>
                    <Typography.Paragraph strong>
                      Wind X (-5.0 to 5.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.windDirectionX}
                      min={-5.0}
                      max={5.0}
                      step={0.1}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('windDirectionX', value)
                      }
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Wind Y (-5.0 to 5.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.windDirectionY}
                      min={-5.0}
                      max={5.0}
                      step={0.1}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('windDirectionY', value)
                      }
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Wind Z (-5.0 to 5.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.windDirectionZ}
                      min={-5.0}
                      max={5.0}
                      step={0.1}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('windDirectionZ', value)
                      }
                    />
                  </div>
                </Flex>
              </Panel>

              <Panel header='Spawn Area' key='spawn'>
                <Flex vertical gap={12}>
                  <Typography.Text strong>Minimum Bounds</Typography.Text>
                  <div>
                    <Typography.Paragraph>
                      Min X (-2000 to 0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.spawnAreaMinX}
                      min={-2000}
                      max={0}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('spawnAreaMinX', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph>
                      Min Y (0 to 1000)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.spawnAreaMinY}
                      min={0}
                      max={1000}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('spawnAreaMinY', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph>
                      Min Z (-2000 to 0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.spawnAreaMinZ}
                      min={-2000}
                      max={0}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('spawnAreaMinZ', value)}
                    />
                  </div>

                  <Typography.Text strong style={{ marginTop: 16 }}>
                    Maximum Bounds
                  </Typography.Text>
                  <div>
                    <Typography.Paragraph>
                      Max X (0 to 2000)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.spawnAreaMaxX}
                      min={0}
                      max={2000}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('spawnAreaMaxX', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph>
                      Max Y (200 to 1500)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.spawnAreaMaxY}
                      min={200}
                      max={1500}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('spawnAreaMaxY', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph>
                      Max Z (0 to 2000)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.spawnAreaMaxZ}
                      min={0}
                      max={2000}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('spawnAreaMaxZ', value)}
                    />
                  </div>
                </Flex>
              </Panel>

              <Panel header='Noise & Variation' key='noise'>
                <Flex vertical gap={12}>
                  <div>
                    <Typography.Paragraph strong>
                      Threshold (0.0-1.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.threshold}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('threshold', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Range (0.0-1.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.range}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('range', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Steps (10-500)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.steps}
                      min={10}
                      max={500}
                      style={{ width: '100%' }}
                      onChange={(value) => handleChange('steps', value)}
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Opacity Variation (0.0-1.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.variationOpacity}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('variationOpacity', value)
                      }
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Scale Variation (0.0-2.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.variationScale}
                      min={0.0}
                      max={2.0}
                      step={0.01}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('variationScale', value)
                      }
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Threshold Variation (0.0-1.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.variationThreshold}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('variationThreshold', value)
                      }
                    />
                  </div>
                  <div>
                    <Typography.Paragraph strong>
                      Color Variation (0.0-1.0)
                    </Typography.Paragraph>
                    <InputNumber
                      value={currentCloudConfig.variationColor}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      style={{ width: '100%' }}
                      onChange={(value) =>
                        handleChange('variationColor', value)
                      }
                    />
                  </div>
                </Flex>
              </Panel>
            </Collapse>
          )}
        </Flex>
      )}
    </>
  );
}
